import { Col, Segmented, Select, Space, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useProductSearch, {
  TAMANHO_MINIMO_DA_BUSCA,
} from "../../hooks/useProductSearch";
import useApi from "../../utils/useApi";
import { CINCO_MINUTOS } from "../../utils/useApiV2";
import {
  OPCOES_DE_MECANICA,
  rotuloDaMecanica,
} from "../collections-change/promoHelpers";
import { OPCOES_DE_ESCOPO } from "./escopoHelpers";

const { Text } = Typography;

function SeletorDeSkus({ valor, onChange, placeholder }) {
  const [busca, setBusca] = useState("");
  const buscaAtrasada = useDebouncedValue(busca, 300);
  const produtos = useProductSearch({ q: buscaAtrasada });
  const [rotulos, setRotulos] = useState({});

  useEffect(() => {
    const lista = produtos.data?.products;
    if (!Array.isArray(lista) || lista.length === 0) return;
    setRotulos((atuais) => {
      const proximos = { ...atuais };
      lista.forEach((produto) => {
        proximos[produto.produto] = `${produto.produto} · ${produto.descricao}`;
      });
      return proximos;
    });
  }, [produtos.data]);

  const opcoes = useMemo(() => {
    const codigos = new Set([
      ...valor,
      ...(produtos.data?.products || []).map((produto) => produto.produto),
    ]);
    return Array.from(codigos).map((codigo) => ({
      value: codigo,
      label: rotulos[codigo] || codigo,
    }));
  }, [valor, produtos.data, rotulos]);

  const buscaCurta = buscaAtrasada.trim().length < TAMANHO_MINIMO_DA_BUSCA;

  return (
    <Select
      mode="multiple"
      style={{ width: "100%" }}
      showSearch
      filterOption={false}
      autoClearSearchValue={false}
      onSearch={setBusca}
      onChange={onChange}
      value={valor}
      options={opcoes}
      loading={produtos.isFetching}
      placeholder={placeholder}
      notFoundContent={
        buscaCurta
          ? "Digite ao menos 2 caracteres do código, EAN ou descrição"
          : produtos.isFetching
            ? null
            : "Nenhum produto encontrado"
      }
    />
  );
}

export default function EscopoDoEvento({
  valor,
  onChange,
  tipoTravado = false,
  somenteLeitura = false,
  resumo = null,
  motivoDaLeitura = null,
}) {
  const { user } = useAuth();
  const { getCollections, getOuranCatalogPilot } = useApi();

  // A filial segue o próprio modo (decisão 2.3, mesma regra de
  // ManualSkuAssignment): em modo Ouran, "Categoria" busca no catálogo
  // global em vez da coleção do ERP — mesma tela, fonte diferente.
  const pilot = useQuery({
    queryKey: ["ouranCatalogPilotParaEscopo", user?.active_branch_id ?? null],
    queryFn: () => getOuranCatalogPilot().catch(() => null),
    enabled: !somenteLeitura && valor.tipo === "colecao",
    staleTime: CINCO_MINUTOS,
  });
  const modoOuran = pilot.data?.category_mode === "ouran";

  useEffect(() => {
    // Só sincroniza modoOuran num escopo ainda vazio (criação nova) — edição
    // de evento existente já chega com modoOuran certo (a partir de qual
    // campo veio preenchido do back) e não pode ser sobrescrita aqui.
    if (
      valor.tipo === "colecao" &&
      valor.colecao == null &&
      pilot.data &&
      valor.modoOuran !== modoOuran
    ) {
      onChange({ ...valor, modoOuran });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoOuran, pilot.data, valor.tipo, valor.colecao]);

  const colecoes = useQuery({
    queryKey: [
      "colecoesComCodigoErp",
      user?.user_id ?? null,
      user?.active_branch_id ?? null,
    ],
    queryFn: () => getCollections(),
    select: (resposta) =>
      (resposta?.data || [])
        .filter(
          (colecao) => colecao.col_id !== null && colecao.col_id !== undefined
        )
        .map((colecao) => ({ value: colecao.value, label: colecao.label })),
    enabled: !somenteLeitura && valor.tipo === "colecao" && !modoOuran,
    staleTime: CINCO_MINUTOS,
  });

  const categoriasOuran = (pilot.data?.categories || []).map((categoria) => ({
    value: categoria.id,
    label: categoria.name,
  }));

  const alterar = (campos) => onChange({ ...valor, ...campos });

  if (somenteLeitura) {
    const mecanica = rotuloDaMecanica(valor.mecanica);
    return (
      <>
        <Col span={4}>
          <Text strong>Escopo: </Text>
        </Col>
        <Col span={20}>
          <Space wrap>
            <Tag>{resumo || "Produtos"}</Tag>
            {mecanica && <Tag>{mecanica}</Tag>}
            {motivoDaLeitura && <Text type="secondary">{motivoDaLeitura}</Text>}
          </Space>
        </Col>
      </>
    );
  }

  return (
    <>
      <Col span={4}>
        <Text strong>Escopo: </Text>
      </Col>
      <Col span={20}>
        <Segmented
          options={OPCOES_DE_ESCOPO}
          value={valor.tipo}
          disabled={tipoTravado}
          onChange={(tipo) => alterar({ tipo })}
        />
      </Col>

      {valor.tipo === "sku" && (
        <>
          <Col span={4}>
            <Text strong>SKUs: </Text>
          </Col>
          <Col span={20}>
            <SeletorDeSkus
              valor={valor.skus}
              onChange={(skus) => alterar({ skus })}
              placeholder="Busque pelo código, EAN ou descrição"
            />
          </Col>
        </>
      )}

      {valor.tipo === "colecao" && (
        <>
          <Col span={4}>
            <Text strong>Categoria: </Text>
          </Col>
          <Col span={20}>
            <Select
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              options={modoOuran ? categoriasOuran : colecoes.data}
              loading={modoOuran ? pilot.isLoading : colecoes.isLoading}
              value={valor.colecao}
              onChange={(colecao) => alterar({ colecao })}
              placeholder={
                modoOuran
                  ? "Categoria do catálogo Ouran"
                  : "Categoria com código no ERP"
              }
            />
          </Col>
          <Col span={4}>
            <Text strong>Exceto os SKUs: </Text>
          </Col>
          <Col span={20}>
            <SeletorDeSkus
              valor={valor.exclusoes}
              onChange={(exclusoes) => alterar({ exclusoes })}
              placeholder="Opcional"
            />
          </Col>
        </>
      )}

      {valor.tipo !== "zona" && (
        <>
          <Col span={4}>
            <Text strong>Mecânica: </Text>
          </Col>
          <Col span={20}>
            <Select
              style={{ width: "100%" }}
              allowClear
              options={OPCOES_DE_MECANICA}
              value={valor.mecanica}
              onChange={(mecanica) => alterar({ mecanica: mecanica ?? null })}
              placeholder="Opcional"
            />
          </Col>
        </>
      )}
    </>
  );
}
